import { useFormContext, Controller } from "react-hook-form";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import Divider from "@mui/material/Divider";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import useTheme from "@mui/material/styles/useTheme";

const HeatPump = () => {
  const { control, watch } = useFormContext();
  const { t } = useTranslation();
  const theme = useTheme();

  // Local state for toggle switches
  const [airTempInWinter, setAirTempInWinter] = useState(false);
  const [airTempInSummer, setAirTempInSummer] = useState(false);

  // Watch loading and error states from form
  const isLoading = watch("heatPumpLoading") || false;
  const error = watch("heatPumpError");

  // Watch form values for Winter Medium Data (used in render)
  const heatPumpHeaterType = watch("heatPumpHeaterType");
  const heatPumpHeaterWaterInputParam = watch("heatPumpHeaterWaterInputParam");

  // Validation rules
  const validationRules = {
    heatPumpTemperatureInWinter: {
      required: t("validation.required") || "Required",
    },
    heatPumpTemperatureInSummer: {
      required: t("validation.required") || "Required",
    },
    heatPumpHumidityInWinter: {
      required: t("validation.required") || "Required",
    },
    heatPumpHumidityInSummer: {
      required: t("validation.required") || "Required",
    },
    heatPumpSetPointWinter: {
      required: t("validation.required") || "Required",
    },
    heatPumpSetPointSummer: {
      required: t("validation.required") || "Required",
    },
    heatPumpEvaporatingWinter: {
      required: t("validation.required") || "Required",
    },
    heatPumpCondensingWinter: {
      required: t("validation.required") || "Required",
    },
    heatPumpEvaporatingSummer: {
      required: t("validation.required") || "Required",
    },
    heatPumpCondensingSummer: {
      required: t("validation.required") || "Required",
    },
    heatPumpMediumType: {
      required: t("validation.required") || "Required",
    },
    heatPumpHeaterType: {
      required: t("validation.required") || "Required",
    },
    heatPumpHeaterConcentration: {
      required: t("validation.required") || "Required",
    },
    heatPumpHeaterTemperatureIn: {
      required: t("validation.required") || "Required",
    },
    heatPumpHeaterWaterInputParamValue: {
      required: t("validation.required") || "Required",
    },
  };

  // Refrigerant type options
  const refrigerantTypeOptions = [
    { value: "R410A", label: "R410A" },
    { value: "R454B", label: "R454B" },
  ];

  // Heating types for Winter Medium Data
  const heatingTypes = [
    { value: "water", label: t("operatingpoint.heatingType.water") },
    { value: "ethylene", label: t("operatingpoint.heatingType.ethylene") },
  ];

  return (
    <Paper sx={{ p: { xs: 1, md: 5 } }}>
      {/* Title */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          mb: 2,
        }}
      >
        <Typography sx={{ m: "16px", fontSize: "1.4rem" }}>
          {t("mainform.heatPump.title") || "Heat pump"}
        </Typography>
      </Box>

      {/* Error Display */}
      {error && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}

      {/* Loading Indicator */}
      {isLoading && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            py: 4,
          }}
        >
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>
            {t("heatPump.calculating") ||
              "please wait, calculation in progress"}
          </Typography>
        </Box>
      )}

      {/* Main Content */}
      {!isLoading && (
        <Box style={{ display: "flex", justifyContent: "center", gap: "10px" }}>
          {/* Winter Section */}
          <Paper
            elevation={0}
            sx={{
              width: { xl: "50%", xs: "100%" },
            }}
          >
            <Box
              sx={{
                width: { xl: "100%", xs: "100%" },
                mt: { sm: 1, xl: 0 },
                ml: { sm: 0, xl: 1 },
                mb: { sm: 0, xl: 1 },
              }}
            >
              <Typography sx={{ m: "16px", fontSize: "1.4rem" }}>
                Winter
              </Typography>
              <Typography sx={{ m: "16px" }}>{t("coils.title")}</Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={airTempInWinter}
                    onChange={() => setAirTempInWinter(!airTempInWinter)}
                  />
                }
                label={t("coils.manualChange")}
              />

              {/* Temperature In */}
              <Box sx={{ mb: { xl: 1, sm: 1 } }}>
                <Controller
                  name="heatPumpTemperatureInWinter"
                  control={control}
                  rules={validationRules.heatPumpTemperatureInWinter}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      fullWidth
                      disabled={!airTempInWinter}
                      label={
                        t("coils.airTemperatureIn") || "Air temperature in"
                      }
                      type="number"
                      InputLabelProps={{ shrink: true }}
                      error={!!error}
                      helperText={error?.message}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="start">°C</InputAdornment>
                        ),
                        onWheel: (e) => e.target.blur(),
                      }}
                      inputProps={{
                        step: 0.1,
                      }}
                    />
                  )}
                />
              </Box>

              {/* Humidity In */}
              <Box sx={{ mb: { xl: 1, sm: 1 } }}>
                <Controller
                  name="heatPumpHumidityInWinter"
                  control={control}
                  rules={validationRules.heatPumpHumidityInWinter}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      fullWidth
                      disabled={!airTempInWinter}
                      label={
                        t("coils.relativeHumidityIn") || "Relative humidity in"
                      }
                      type="number"
                      InputLabelProps={{ shrink: true }}
                      error={!!error}
                      helperText={error?.message}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="start">%</InputAdornment>
                        ),
                        onWheel: (e) => e.target.blur(),
                      }}
                      inputProps={{
                        step: 0.1,
                      }}
                    />
                  )}
                />
              </Box>

              <Divider sx={{ mb: 2 }} />

              {/* Air Input Parameter - Fixed to "Outlet air coil temperature" */}
              <FormControl fullWidth>
                <InputLabel id="heatPumpAirInputParamWinter-label">
                  {t("coils.airInputParameter")}
                </InputLabel>
                <Select
                  labelId="heatPumpAirInputParamWinter-label"
                  id="heatPumpAirInputParamWinter"
                  value="Supply Temperature"
                  disabled
                  label={t("coils.airInputParameter")}
                >
                  <MenuItem value="Supply Temperature">
                    {t("coils.supplyCoilTemperature")}
                  </MenuItem>
                </Select>
              </FormControl>

              {/* Set Point */}
              <Box sx={{ mt: { sm: 1, xl: 1 } }}>
                <Controller
                  name="heatPumpSetPointWinter"
                  control={control}
                  rules={validationRules.heatPumpSetPointWinter}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label={
                        t("coils.supplyCoilTemperature") ||
                        "Outlet air coil temperature"
                      }
                      type="number"
                      InputLabelProps={{ shrink: true }}
                      error={!!error}
                      helperText={error?.message}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="start">°C</InputAdornment>
                        ),
                        onWheel: (e) => e.target.blur(),
                      }}
                      inputProps={{
                        step: 0.1,
                      }}
                    />
                  )}
                />
              </Box>

              {/* Refrigerant Data Section */}
              <Box sx={{ mt: { xl: 1, sm: 1 } }}>
                <Typography sx={{ m: "16px" }}>
                  {t("mainform.heatPump.refrigerantData") || "Refrigerant data"}
                </Typography>

                {/* Evaporating Temperature */}
                <Box sx={{ mb: { xl: 1, sm: 1 } }}>
                  <Controller
                    name="heatPumpEvaporatingWinter"
                    control={control}
                    rules={validationRules.heatPumpEvaporatingWinter}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Evaporating temperature (Te)"
                        type="number"
                        InputLabelProps={{ shrink: true }}
                        error={!!error}
                        helperText={error?.message}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="start">°C</InputAdornment>
                          ),
                          onWheel: (e) => e.target.blur(),
                        }}
                        inputProps={{
                          step: 0.1,
                        }}
                      />
                    )}
                  />
                </Box>

                {/* Condensating Temperature */}
                <Box sx={{ mb: { xl: 1, sm: 1 } }}>
                  <Controller
                    name="heatPumpCondensingWinter"
                    control={control}
                    rules={validationRules.heatPumpCondensingWinter}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Condensating temperature (Tc)"
                        type="number"
                        InputLabelProps={{ shrink: true }}
                        error={!!error}
                        helperText={error?.message}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="start">°C</InputAdornment>
                          ),
                          onWheel: (e) => e.target.blur(),
                        }}
                        inputProps={{
                          step: 0.1,
                        }}
                      />
                    )}
                  />
                </Box>
              </Box>

              {/* Medium Data Section */}
              {/* <Box sx={{ mt: { xl: 1, sm: 1 } }}>
                <Divider sx={{ mb: 2 }} />
                <FormControl fullWidth>
                  <InputLabel id="heatPumpHeaterType-label">
                    {t("operatingpoint.heaterType")}
                  </InputLabel>
                  <Controller
                    name="heatPumpHeaterType"
                    control={control}
                    rules={validationRules.heatPumpHeaterType}
                    render={({ field, fieldState: { error } }) => (
                      <Select
                        {...field}
                        sx={{
                          width: { xl: "100%", xs: "100%" },
                          display: "block",
                          overflow: "hidden",
                        }}
                        labelId="heatPumpHeaterType-label"
                        id="heatPumpHeaterType"
                        label={t("operatingpoint.heaterType")}
                        error={!!error}
                        MenuProps={{
                          disableScrollLock: true,
                        }}
                      >
                        {heatingTypes.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                </FormControl>
              </Box> */}
              {/* <Box
                sx={{
                  display: { xl: "flex", xs: "block", gap: "0%" },
                  backgroundColor: theme.palette.primary.light,
                }}
              >
                <Box
                  sx={{
                    width: { xl: "50%", xs: "100%" },
                    mt: { sm: 1, xl: 1 },
                    mr: { sm: 1, xl: 1 },
                  }}
                >
                  <Controller
                    name="heatPumpHeaterConcentration"
                    control={control}
                    rules={validationRules.heatPumpHeaterConcentration}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        id="heatPumpHeaterConcentration"
                        label={t("operatingpoint.concentration")}
                        disabled={heatPumpHeaterType === "water" ? true : false}
                        type="number"
                        InputLabelProps={{ shrink: true }}
                        error={!!error}
                        helperText={error?.message}
                        inputProps={{
                          step: 1,
                        }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="start">%</InputAdornment>
                          ),
                          onWheel: (e) => e.target.blur(),
                        }}
                      />
                    )}
                  />
                </Box>
                <Box
                  sx={{
                    width: { xl: "50%", xs: "100%" },
                    mt: { sm: 1, xl: 1 },
                  }}
                >
                  <Controller
                    name="heatPumpHeaterWaterInputParam"
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth>
                        <InputLabel id="heatPumpHeaterWaterInputParam-label">
                          {t("coils.waterInputParameter")}
                        </InputLabel>
                        <Select
                          {...field}
                          labelId="heatPumpHeaterWaterInputParam-label"
                          id="heatPumpHeaterWaterInputParam"
                          label={t("coils.waterInputParameter")}
                          MenuProps={{
                            disableScrollLock: true,
                          }}
                        >
                          <MenuItem value="Temperature out">
                            {t("coils.temperatureOut")}
                          </MenuItem>
                          <MenuItem value="Fluid flow">
                            {t("coils.fluidFlow")}
                          </MenuItem>
                        </Select>
                      </FormControl>
                    )}
                  />
                </Box>
              </Box>
              <Box
                sx={{
                  display: { xl: "flex", xs: "block" },
                  backgroundColor: theme.palette.primary.light,
                }}
              >
                <Box
                  sx={{
                    width: { xl: "50%", xs: "100%" },
                    mt: { sm: 1, xl: 1 },
                    mr: { sm: 1, xl: 1 },
                  }}
                >
                  <Controller
                    name="heatPumpHeaterTemperatureIn"
                    control={control}
                    rules={validationRules.heatPumpHeaterTemperatureIn}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        id="heatPumpHeaterTemperatureIn"
                        label={t("operatingpoint.temperatureIn")}
                        type="number"
                        InputLabelProps={{ shrink: true }}
                        error={!!error}
                        helperText={error?.message}
                        inputProps={{
                          step: 1,
                        }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="start">°C</InputAdornment>
                          ),
                          onWheel: (e) => e.target.blur(),
                        }}
                      />
                    )}
                  />
                </Box>
                <Box
                  sx={{
                    width: { xl: "50%", xs: "100%" },
                    mt: { sm: 1, xl: 1 },
                  }}
                >
                  <Controller
                    name="heatPumpHeaterWaterInputParamValue"
                    control={control}
                    rules={validationRules.heatPumpHeaterWaterInputParamValue}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        id="heatPumpHeaterWaterInputParamValue"
                        label={
                          heatPumpHeaterWaterInputParam === "Temperature out"
                            ? t("operatingpoint.temperatureOut")
                            : t("operatingpoint.fluidFlow")
                        }
                        type="number"
                        InputLabelProps={{ shrink: true }}
                        error={!!error}
                        helperText={error?.message}
                        inputProps={{
                          step: 1,
                        }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="start">
                              {heatPumpHeaterWaterInputParam ===
                              "Temperature out"
                                ? "°C"
                                : "m³/h"}
                            </InputAdornment>
                          ),
                          onWheel: (e) => e.target.blur(),
                        }}
                      />
                    )}
                  />
                </Box>
              </Box> */}
            </Box>
          </Paper>

          {/* Summer Section */}
          <Paper
            elevation={0}
            sx={{
              width: { xl: "50%", xs: "100%" },
            }}
          >
            <Box
              sx={{
                width: { xl: "100%", xs: "100%" },
                mt: { sm: 1, xl: 0 },
                ml: { sm: 0, xl: 1 },
                mb: { sm: 0, xl: 1 },
              }}
            >
              <Typography sx={{ m: "16px", fontSize: "1.4rem" }}>
                Summer
              </Typography>
              <Typography sx={{ m: "16px" }}>{t("coils.title")}</Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={airTempInSummer}
                    onChange={() => setAirTempInSummer(!airTempInSummer)}
                  />
                }
                label={t("coils.manualChange")}
              />

              {/* Temperature In */}
              <Box sx={{ mb: { xl: 1, sm: 1 } }}>
                <Controller
                  name="heatPumpTemperatureInSummer"
                  control={control}
                  rules={validationRules.heatPumpTemperatureInSummer}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      fullWidth
                      disabled={!airTempInSummer}
                      label={
                        t("coils.airTemperatureIn") || "Air temperature in"
                      }
                      type="number"
                      InputLabelProps={{ shrink: true }}
                      error={!!error}
                      helperText={error?.message}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="start">°C</InputAdornment>
                        ),
                        onWheel: (e) => e.target.blur(),
                      }}
                      inputProps={{
                        step: 0.1,
                      }}
                    />
                  )}
                />
              </Box>

              {/* Humidity In */}
              <Box sx={{ mb: { xl: 1, sm: 1 } }}>
                <Controller
                  name="heatPumpHumidityInSummer"
                  control={control}
                  rules={validationRules.heatPumpHumidityInSummer}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      fullWidth
                      disabled={!airTempInSummer}
                      label={
                        t("coils.relativeHumidityIn") || "Relative humidity in"
                      }
                      type="number"
                      InputLabelProps={{ shrink: true }}
                      error={!!error}
                      helperText={error?.message}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="start">%</InputAdornment>
                        ),
                        onWheel: (e) => e.target.blur(),
                      }}
                      inputProps={{
                        step: 0.1,
                      }}
                    />
                  )}
                />
              </Box>

              <Divider sx={{ mb: 2 }} />

              {/* Air Input Parameter - Fixed to "Outlet air coil temperature" */}
              <FormControl fullWidth>
                <InputLabel id="heatPumpAirInputParamSummer-label">
                  {t("coils.airInputParameter")}
                </InputLabel>
                <Select
                  labelId="heatPumpAirInputParamSummer-label"
                  id="heatPumpAirInputParamSummer"
                  value="Supply Temperature"
                  disabled
                  label={t("coils.airInputParameter")}
                >
                  <MenuItem value="Supply Temperature">
                    {t("coils.supplyCoilTemperature")}
                  </MenuItem>
                </Select>
              </FormControl>

              {/* Set Point */}
              <Box sx={{ mt: { sm: 1, xl: 1 } }}>
                <Controller
                  name="heatPumpSetPointSummer"
                  control={control}
                  rules={validationRules.heatPumpSetPointSummer}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label={
                        t("coils.supplyCoilTemperature") ||
                        "Outlet air coil temperature"
                      }
                      type="number"
                      InputLabelProps={{ shrink: true }}
                      error={!!error}
                      helperText={error?.message}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="start">°C</InputAdornment>
                        ),
                        onWheel: (e) => e.target.blur(),
                      }}
                      inputProps={{
                        step: 0.1,
                      }}
                    />
                  )}
                />
              </Box>

              {/* Refrigerant Data Section */}
              <Box sx={{ mt: { xl: 1, sm: 1 } }}>
                <Typography sx={{ m: "16px" }}>
                  {t("mainform.heatPump.refrigerantData") || "Refrigerant data"}
                </Typography>

                {/* Evaporating Temperature */}
                <Box sx={{ mb: { xl: 1, sm: 1 } }}>
                  <Controller
                    name="heatPumpEvaporatingSummer"
                    control={control}
                    rules={validationRules.heatPumpEvaporatingSummer}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Evaporating temperature (Te)"
                        type="number"
                        InputLabelProps={{ shrink: true }}
                        error={!!error}
                        helperText={error?.message}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="start">°C</InputAdornment>
                          ),
                          onWheel: (e) => e.target.blur(),
                        }}
                        inputProps={{
                          step: 0.1,
                        }}
                      />
                    )}
                  />
                </Box>

                {/* Condensating Temperature */}
                <Box sx={{ mb: { xl: 1, sm: 1 } }}>
                  <Controller
                    name="heatPumpCondensingSummer"
                    control={control}
                    rules={validationRules.heatPumpCondensingSummer}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Condensating temperature (Tc)"
                        type="number"
                        InputLabelProps={{ shrink: true }}
                        error={!!error}
                        helperText={error?.message}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="start">°C</InputAdornment>
                          ),
                          onWheel: (e) => e.target.blur(),
                        }}
                        inputProps={{
                          step: 0.1,
                        }}
                      />
                    )}
                  />
                </Box>
              </Box>
            </Box>
          </Paper>
        </Box>
      )}

      {/* Refrigerant Properties Block - Below both sections */}
      {!isLoading && (
        <Box sx={{ mt: { xl: 1, sm: 1 } }}>
          <Divider sx={{ mb: 2 }} />
          <Box
            sx={{
              display: { xl: "flex", xs: "block" },
              backgroundColor: "transparent",
            }}
          >
            {/* Refrigerant Type */}
            <Box
              sx={{
                width: { xl: "50%", xs: "100%" },
                mt: { sm: 1, xl: 1 },
                mr: { sm: 1, xl: 1 },
              }}
            >
              <Controller
                name="heatPumpMediumType"
                control={control}
                rules={validationRules.heatPumpMediumType}
                render={({ field, fieldState: { error } }) => (
                  <FormControl fullWidth error={!!error}>
                    <InputLabel id="heatPumpMediumType-label">Type</InputLabel>
                    <Select
                      {...field}
                      labelId="heatPumpMediumType-label"
                      id="heatPumpMediumType"
                      label="Type"
                      MenuProps={{
                        disableScrollLock: true,
                      }}
                    >
                      {refrigerantTypeOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            </Box>

            {/* Power Supply - Static Text */}
            <Box
              sx={{
                width: { xl: "50%", xs: "100%" },
                mt: { sm: 1, xl: 1 },
                display: "flex",
                alignItems: "center",
              }}
            >
              <Typography sx={{ m: "16px" }}>380–480 V, 50 Hz, 3 ph</Typography>
            </Box>
          </Box>
        </Box>
      )}
    </Paper>
  );
};

export default HeatPump;
